import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import analytics from '@repo/analytics';
import { DEFAULT_TAKE } from '@repo/editor/src/global/constants';
import useWindowSize from '@repo/editor/src/hooks/useWindowSize';
import {
  authenticationStoreSelector,
  useAuthenticationStore,
} from '@repo/editor/src/stores/authenticationStore';
import {
  designsStoreSelector,
  useDesignsStore,
} from '@repo/editor/src/stores/designsStore';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { Post } from '@repo/editor/src/types';
import { withEventScope } from '@repo/event-processor';
import { P2 } from '@repo/ui/components/Typography';
import { themeStyle } from '@repo/ui/theming';
import isEqual from 'lodash/isEqual';

import FeedBody from 'components/FeedBody/FeedBody';
import NewProjectTile from 'components/NewProjectTile/NewProjectTile';
import { useFilter } from 'components/NewTemplatesPage/Filter/FilterContext';
import { SEARCH_TEMPLATES_WEBSITE } from 'global/analyticsEvents';
import postApi from 'global/postApi';
import { buildEditorUrlWithTemplateParams } from 'global/urlUtils';
import { findActiveTemplateParam } from 'global/utils/templateParams';
import useSearch from 'global/utils/useSearch';

import { TemplatesFeedProps } from './type';

const TemplatesFeed: React.FC<TemplatesFeedProps> = ({
  unauthorizedView,
  initialDesignsFeed,
  sort,
  seed,
  filters,
  refineByTags,
  currentSelection,
}) => {
  const { isFilterVisible } = useFilter();

  const user = useUserStore(userStoreSelector.loadingUser);
  const updateUserInfo = useUserStore(userStoreSelector.updateUserInfo);
  const openAuthentication = useAuthenticationStore(
    authenticationStoreSelector.openAuthentication
  );
  const canUserCreateDesign = useDesignsStore(
    designsStoreSelector.canUserCreateDesign
  );

  const { isMobile } = useWindowSize();
  const { query } = useSearch();

  const showInitialDesignFeed =
    unauthorizedView &&
    !!initialDesignsFeed &&
    initialDesignsFeed.length &&
    !query;

  const [posts, setPosts] = useState<Post[]>(
    showInitialDesignFeed ? initialDesignsFeed : []
  );
  const [finished, setFinished] = useState(false);
  const [loading, setLoading] = useState(!showInitialDesignFeed);
  const unmounted = useRef(false);

  const tags = useMemo(() => {
    return Object.keys(filters).map((key) => filters[key]);
  }, [filters]);

  const tagLabels = useMemo(() => {
    return tags.filter((tag) => tag.id !== 'all').map((tag) => tag.label);
  }, [tags]);

  // get the last slug in array which is not 'all'
  const activeTemplateParam = useMemo(
    () =>
      findActiveTemplateParam(
        currentSelection.subUseCaseSlug,
        currentSelection.useCaseSlug,
        currentSelection.subCategorySlug,
        currentSelection.categorySlug
      ),
    [currentSelection]
  );

  const {
    useCaseSlug: currentUseCaseSlug,
    subUseCaseSlug: currentSubUseCaseSlug,
  } = currentSelection;

  const fetchState = useRef({
    skip: showInitialDesignFeed ? initialDesignsFeed.length : 0,
    take: DEFAULT_TAKE,
    isFetching: false,
    query,
    sort: sort,
    activeTemplateParam,
    tagLabels: tagLabels,
  });

  // Fetch templates
  const getTemplates = useCallback(async () => {
    const { skip, take, isFetching } = fetchState.current;

    if (isFetching || finished) {
      return;
    }
    fetchState.current.isFetching = true;

    // If query is empty, use explore, else use searchFeed
    const getTemplatesApi = () =>
      (query ? postApi.searchFeed : postApi.explore)({
        query,
        take,
        skip,
        sort,
        seed,
        useCaseSlug: activeTemplateParam,
        tags: tagLabels,
      });

    const response = await getTemplatesApi();

    if (!response.success) {
      return;
    }

    if (query && skip === 0) {
      analytics.track(SEARCH_TEMPLATES_WEBSITE, {
        label: query,
        value: response?.results?.length,
        category: currentSelection.categorySlug,
        subCategory: currentSelection.subCategorySlug,
        useCase: currentSelection.useCaseSlug,
        subUseCase: currentSelection.subUseCaseSlug,
        tags: tagLabels,
      });
    }

    // If component has been unmounted or queryWithTags has changed, return
    if (unmounted.current || fetchState.current.query !== query) {
      return;
    }

    const tagLabelsFromResponse = response.tags?.map((tag) => tag.name) || [];

    // If use case or tagLabels has changed, return
    // !query because searchFeed route above will not have useCaseSlug and tags in response
    if (
      !query &&
      (fetchState.current.activeTemplateParam !== response.useCase?.slug ||
        !isEqual(
          fetchState.current.tagLabels.sort(),
          tagLabelsFromResponse.sort()
        ))
    ) {
      return;
    }

    // If sort has changed, return
    if (response.sort && response.sort !== fetchState.current.sort) {
      return;
    }

    if (!response.results?.length) {
      // If no results, set finished
      setFinished(true);
    } else {
      // otherwise, add the results to the posts and update the skip
      setPosts((posts) => [
        ...posts,
        ...response.results.filter((res) => !!res.template),
      ]);
      fetchState.current.skip += take;
    }

    setLoading(false);

    fetchState.current.isFetching = false;
  }, [
    finished,
    query,
    sort,
    seed,
    activeTemplateParam,
    tagLabels,
    currentSelection,
  ]);

  const restoreItems = () => {
    fetchState.current.skip = 0;
    fetchState.current.isFetching = false;
    setFinished(false);
    setPosts([]);
    setLoading(true);
  };

  // If use case changes, restore items and fetch templates
  useEffect(() => {
    if (fetchState.current.activeTemplateParam !== activeTemplateParam) {
      // Send analytics when use case changes
      fetchState.current.activeTemplateParam = activeTemplateParam;
      restoreItems();
    }
  }, [activeTemplateParam]);

  // If query changes, restore items and fetch templates
  useEffect(() => {
    if (query !== fetchState.current.query) {
      // Send analytics when queryWithTags changes
      fetchState.current.query = query;
      restoreItems();
    }
  }, [query]);

  // If tagLabels change, restore items and fetch templates
  useEffect(() => {
    if (!isEqual(tagLabels, fetchState.current.tagLabels)) {
      fetchState.current.tagLabels = tagLabels;
      restoreItems();
    }
  }, [tagLabels]);

  // If sort changes, restore items and fetch templates
  useEffect(() => {
    if (sort !== fetchState.current.sort) {
      fetchState.current.sort = sort;
      restoreItems();
    }
  }, [sort]);

  // Fetch templates
  useEffect(() => {
    if (!posts.length && user !== false) {
      void getTemplates();
    }
  }, [posts, user, getTemplates]);

  // Cleanup
  useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
    };
  }, []);

  if (loading || !posts.length) {
    return (
      <P2
        color={themeStyle.varInkMedium}
        style={{ textAlign: 'center', margin: '150px auto' }}
      >
        {loading ? 'Loading Content ...' : 'Could not find any posts.'}
      </P2>
    );
  }

  const handleNewProjectClick = () => {
    if (!user) {
      openAuthentication({
        onSubmit: async () => {
          const updatedUser = await updateUserInfo();
          if (updatedUser?.designsLimitReached) {
            canUserCreateDesign();
          } else {
            window.open(
              buildEditorUrlWithTemplateParams({
                useCase: currentUseCaseSlug,
                subUseCase: currentSubUseCaseSlug,
              }),
              '_blank'
            );
          }
        },
      });
    }
    return;
  };

  const newProjectButton = <NewProjectTile onClick={handleNewProjectClick} />;

  const firstElement = unauthorizedView
    ? newProjectButton
    : !isMobile
      ? refineByTags
      : undefined;

  return (
    <>
      <FeedBody
        eagerImagesLoading
        clickAction={isMobile ? 'postDetails' : 'openEditor'}
        currentSubUseCase={currentSubUseCaseSlug}
        currentUseCase={currentUseCaseSlug}
        fetch={getTemplates}
        firstElement={firstElement}
        layout={
          isFilterVisible && !isMobile
            ? 'templatesWithFiltersMasonry'
            : 'templatesMasonry'
        }
        minColumnWidth={isMobile ? 150 : undefined}
        posts={posts}
      />
    </>
  );
};

export default withEventScope(TemplatesFeed, { feature: 'templateFeed' });
